import { styled } from 'styled-components'

import { ContractDtoBidBasisEnum } from '@b-stock/contract-api-client'
import {
  type OrchestratedListingDetails,
  isOfferingListing,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  FormattedMessage,
  usePrecisionNumberFormat,
} from '@b-stock/fe-core/bstock-react'
import { Typography } from '@b-stock/fe-core/design-system'
import { designColors } from '@b-stock/fe-core/theme'
import { ListingDtoListingTypeEnum } from '@b-stock/listing-api-client'

import TextTransition from '@/buyer-portal/components/TextTransition'

const Price = styled.span`
  transition:
    color 300ms ease-out,
    opacity 300ms ease,
    transform 300ms ease-in;

  ${Typography.Title1}
  color: ${designColors.neutral.black};
`
const PricePerUnit = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
`
const PerUnit = styled.div`
  ${Typography.Body1}
  color: ${designColors.neutral.veryDarkGray};
`

const ns = 'Auction.details.bid.details'

export const CurrentPrice = ({
  listing,
  currency = 'USD',
  price,
  units,
  shouldHideNumbers,
}: {
  listing?: OrchestratedListingDetails
  currency?: string
  price: number
  units?: number
  shouldHideNumbers: boolean
}) => {
  const pricePerUnit = units ? price / units : price

  const isContract = listing?.listingType === ListingDtoListingTypeEnum.Contract
  const isRelativeBid =
    isContract &&
    listing.contract?.bidBasis === ContractDtoBidBasisEnum.Relative
  const contractUnit = listing?.contract?.bidUnit

  const isOffering = listing && isOfferingListing(listing)

  const bidPrecision = isOffering
    ? listing.offering?.bidPrecision
    : listing?.auction.bidPrecision

  const { formatPrecisionNumber } = usePrecisionNumberFormat({
    precision: bidPrecision,
    precisionStored: isOffering ? undefined : 1,
    format: isRelativeBid ? 'percent' : 'currency',
    currency,
  })

  const priceIntl = formatPrecisionNumber(price)

  const { formatPrecisionNumber: formatPrecisionNumberPerUnit } =
    usePrecisionNumberFormat({
      precision: isOffering
        ? 1
        : // if pricePerUnit is not an integer, use bidPrecision / 100 (0.01 by default)
          pricePerUnit % 1 !== 0
          ? (bidPrecision ?? 1) / 100
          : bidPrecision,
      precisionStored: isOffering ? undefined : 1,
      format: 'currency',
      currency,
    })
  const pricePerUnitIntl = formatPrecisionNumberPerUnit(pricePerUnit)
  const priceToDisplay = shouldHideNumbers
    ? priceIntl.replace(/[0-9]/g, 'X')
    : priceIntl

  const pricePerUnitToDisplay = shouldHideNumbers
    ? pricePerUnitIntl.replace(/[0-9]/g, 'X')
    : pricePerUnitIntl

  return (
    <PricePerUnit data-testid="price-details">
      <Price>
        {priceToDisplay.split('').map((n, i) => (
          // This is a non-unique array of characters, so we have no choice
          // but to use the array index as a key.
          // eslint-disable-next-line react/no-array-index-key
          <TextTransition key={`price-${i}`} transitionKey={n}>
            {n}
          </TextTransition>
        ))}
        {contractUnit && (
          <FormattedMessage
            id={`Contracts.Enums.perUnit.titleCase.${contractUnit}`}
          />
        )}
      </Price>
      {units && !isContract ? (
        <PerUnit>
          /{' '}
          {pricePerUnitToDisplay.split(' ').map((perUnit, i) => (
            <TextTransition
              // This is a non-unique array of strings, so we have no choice
              // but to use the array index as a key.
              // eslint-disable-next-line react/no-array-index-key
              key={`pricePerUnit-${i}`}
              transitionKey={perUnit}
            >
              <FormattedMessage
                id={`${ns}.perUnit`}
                values={{ pricePerUnit: perUnit }}
              />
            </TextTransition>
          ))}
        </PerUnit>
      ) : null}
    </PricePerUnit>
  )
}
